class ProductGalleryModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.mainGallery = controller.query('[data-main-gallery]');
    this.thumbGallery = controller.query('[data-thumb-gallery]');
    this.pagination = this.mainGallery?.querySelector('.swiper-pagination');
    this.mainSwiper = null;
    this.thumbSwiper = null;

    if (!this.mainGallery || this.getSlides().length <= 1) return;

    this.renderStaticPagination();

    this.section.addEventListener('variant:change', (event) => {
      this.updateGalleryForVariant(event.detail?.variantOption);
    });

    this.init();
  }

  getSlides() {
    return Array.from(this.mainGallery?.querySelectorAll('.swiper-slide') || []);
  }

  renderStaticPagination() {
    if (!this.pagination || this.pagination.children.length > 0) return;

    const slides = this.getSlides();
    const fragment = document.createDocumentFragment();

    slides.forEach((_, index) => {
      const bullet = document.createElement('span');
      bullet.className = `swiper-pagination-bullet${index === 0 ? ' swiper-pagination-bullet-active' : ''}`;
      fragment.appendChild(bullet);
    });

    this.pagination.appendChild(fragment);
  }

  async init() {
    const Swiper = await this.resolveSwiper();
    if (!Swiper) {
      console.warn('Swiper failed to load for ProductSection gallery.');
      return;
    }

    window.requestAnimationFrame(() => {
      if (this.thumbGallery) {
        this.thumbSwiper = new Swiper(this.thumbGallery, {
          spaceBetween: 8,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          observer: true,
          observeParents: true,
          resizeObserver: true,
          threshold: 5,
          touchRatio: 1,
          simulateTouch: true,
          breakpoints: {
            320: { spaceBetween: 6 },
            500: { spaceBetween: 8 },
            901: { spaceBetween: 12 }
          }
        });
      }

      const config = {
        spaceBetween: 0,
        slidesPerView: 1,
        autoHeight: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        pagination: {
          el: this.pagination,
          clickable: true
        },
        navigation: {
          nextEl: this.mainGallery.querySelector('.swiper-button-next'),
          prevEl: this.mainGallery.querySelector('.swiper-button-prev')
        }
      };

      if (this.thumbSwiper) {
        config.thumbs = { swiper: this.thumbSwiper };
      }

      this.mainSwiper = new Swiper(this.mainGallery, config);
      this.initVideoHandlers();
      this.updateGalleryForVariant(this.section.querySelector('[data-variant-select]')?.selectedOptions[0]);
      this.refresh();

      if (document.readyState === 'complete') {
        window.setTimeout(() => this.refresh(), 100);
      } else {
        window.addEventListener(
          'load',
          () => {
            window.setTimeout(() => this.refresh(), 100);
          },
          { once: true }
        );
      }
    });
  }

  async resolveSwiper() {
    if (window.Swiper) return window.Swiper;
    if (typeof window.loadThemeSwiper === 'function') {
      await window.loadThemeSwiper();
    }
    return window.Swiper;
  }

  refresh() {
    this.mainSwiper?.update();
    this.mainSwiper?.updateSize();
    this.thumbSwiper?.update();
    this.thumbSwiper?.updateSize();
  }

  initVideoHandlers() {
    if (!this.mainSwiper) return;

    this.mainSwiper.on('slideChange', () => {
      this.resetAllVideos();
    });

    this.getSlides().forEach((slide) => {
      const video = slide.querySelector('.product-gallery__video');
      const playButton = slide.querySelector('.product-gallery__video-play');
      const videoEmbed = slide.querySelector('.product-gallery__video-embed');

      if (video) {
        video.addEventListener('play', () => this.setPlayButtonVisibility(playButton, false));
        video.addEventListener('pause', () => this.setPlayButtonVisibility(playButton, true));
        video.addEventListener('ended', () => this.setPlayButtonVisibility(playButton, true));

        playButton?.addEventListener('click', () => {
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        });
      }

      if (videoEmbed) {
        const iframe = videoEmbed.querySelector('iframe');
        if (!iframe) return;

        this.mainSwiper.on('slideChangeTransitionStart', () => {
          this.pauseExternalVideo(iframe);
        });

        playButton?.addEventListener('click', () => {
          this.playExternalVideo(iframe);
          this.setPlayButtonVisibility(playButton, false);
        });
      }
    });
  }

  resetAllVideos() {
    this.getSlides().forEach((slide) => {
      const video = slide.querySelector('.product-gallery__video');
      const playButton = slide.querySelector('.product-gallery__video-play');
      const iframe = slide.querySelector('.product-gallery__video-embed iframe');

      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      if (iframe) {
        this.pauseExternalVideo(iframe);
      }

      this.setPlayButtonVisibility(playButton, true);
    });
  }

  setPlayButtonVisibility(button, visible) {
    if (!button) return;
    button.style.display = visible ? 'flex' : 'none';
  }

  playExternalVideo(iframe) {
    if (!iframe?.src) return;

    if (iframe.src.includes('youtube')) {
      iframe.src = iframe.src.replace('?enablejsapi=1', '?enablejsapi=1&autoplay=1');
      return;
    }

    if (iframe.src.includes('vimeo')) {
      iframe.src = iframe.src.includes('?')
        ? `${iframe.src}&autoplay=1`
        : `${iframe.src}?autoplay=1`;
    }
  }

  pauseExternalVideo(iframe) {
    if (!iframe?.src) return;
    iframe.src = iframe.src.replace('&autoplay=1', '').replace('?autoplay=1', '');
  }

  updateGalleryForVariant(variantOption) {
    if (!variantOption || !this.mainSwiper?.slideTo) return;

    const variantId = variantOption.value;
    const option1Value = variantOption.getAttribute('data-option-1') || '';
    const slides = this.getSlides();

    let targetIndex = 0;
    let found = false;

    slides.forEach((slide, index) => {
      if (found) return;

      const variantIds = slide.dataset.variantIds || '';
      const altText = slide.querySelector('img')?.alt || '';

      if (variantIds.includes(variantId)) {
        targetIndex = index;
        found = true;
        return;
      }

      if (option1Value && altText.toLowerCase().includes(option1Value.toLowerCase())) {
        targetIndex = index;
        found = true;
      }
    });

    this.mainSwiper.slideTo(targetIndex);
  }
}

window.HerbiProductSection.registerModule('gallery', (controller) => {
  return new ProductGalleryModule(controller);
});
